import { CompanySearchRequestProperty, ESelectionState, ISystemJob } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import { useDeleteCompaniesMutation } from '../../../../../queries/Company';
import { useExportCompaniesMutation } from '../../../../../queries/Company/useExportCompaniesMutation';
import { MergeCompaniesWizard } from '../../../../components/companies/MergeCompaniesWizard';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { ExportConfirmationModal } from '../../../../components/ExportConfirmation';
import { Modal } from '../../../../components/Modal';
import { ExportIcon } from '../../../../components/svgs/icons/ExportIcon';
import { MergeIcon } from '../../../../components/svgs/icons/MergeIcon';
import { TrashIcon } from '../../../../components/svgs/icons/TrashIcon';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../../components-v2/Dropdown';
import { header } from '../../../../styles/colors';
import { bs } from '../../../../styles/styles';
import { useCompaniesPageUrlState } from '..';
import { useCompaniesSearch } from '../CompaniesContext';
import { buildCompanyTagFilter, hasCompanyTagFilters } from '../companyTagFilter';
import { styleSheet } from './styles';

const useActionBarStatus = () => {
	const { selectionState, selectedCompanyIds } = useCompaniesSearch();
	const isSelectingAll =
		selectionState === ESelectionState.All ||
		(selectionState === ESelectionState.Some && selectedCompanyIds.length === 0);

	const allOrSomeAreSelected = selectedCompanyIds.length > 0;
	const canDelete = allOrSomeAreSelected;
	const canMerge = selectedCompanyIds.length > 1;
	const canExport = allOrSomeAreSelected || isSelectingAll;

	return { canDelete, canExport, canMerge } as const;
};

export const CompaniesActionsBar: React.FC = observer(() => {
	const {
		companies,
		selectedCompanyIds,
		setSelectedCompanyIds,
		selectionState,
		setSelectionState,
		excludedCompanyIds,
		setExcludedCompanyIds,
	} = useCompaniesSearch();
	const errorMessages = useErrorMessages();
	const { canMerge, canDelete, canExport } = useActionBarStatus();
	const userSession = useUserSession();

	const [exportCompaniesPromise, setExportCompaniesPromise] = React.useState<Promise<ISystemJob>>(null);
	const [{ companySearchProperty, companySearchValue, companyOrTags, companyAndTags, companyNotTags }] =
		useCompaniesPageUrlState();

	const tagFilterLists = { orTags: companyOrTags, andTags: companyAndTags, notTags: companyNotTags };
	const hasTagFilters = hasCompanyTagFilters(tagFilterLists);
	// Compose an active name search into the v2 filter as a NamePrefix, mirroring the grid query.
	const namePrefix = companySearchProperty !== CompanySearchRequestProperty.Tag ? companySearchValue : undefined;

	// When exporting "all matching", send exactly ONE filter field — never both. company/export
	// prioritizes `filter` over `deprecatedFilter` and silently ignores the latter when both are set,
	// so we send the v2 recursive criteria for tag search and the legacy text-search filter otherwise.
	// `filter` is only emitted when hasTagFilters is true, so buildCompanyTagFilter never returns an
	// empty/undefined filter here (a non-null empty filter would export the entire account).
	const buildAllMatchingFilter = () =>
		hasTagFilters
			? { filter: buildCompanyTagFilter(tagFilterLists, namePrefix) }
			: { deprecatedFilter: { searches: [{ property: companySearchProperty, value: companySearchValue }] } };

	const deleteCompaniesMutation = useDeleteCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			const count = selectedCompanyIds.length;
			toast.success(`${count} compan${count > 1 ? 'ies' : 'y'} successfully deleted`);
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
		},
	});

	const exportCompaniesMutation = useExportCompaniesMutation({
		impersonationContext: userSession.impersonationContext,
		onSuccess: () => {
			toast.success('Export initiated. You will receive an email when the export is ready.');
			setSelectedCompanyIds([]);
			setSelectionState(ESelectionState.None);
			setExcludedCompanyIds([]);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const mergeModal = useModal(false, () => {
		// noop
	}, [selectedCompanyIds, setSelectedCompanyIds]);

	const deleteModal = useModal(
		false,
		(shouldDelete: boolean) => {
			if (!shouldDelete) {
				return;
			}
			deleteCompaniesMutation.mutate(selectedCompanyIds);
		},
		[selectedCompanyIds, setSelectedCompanyIds]
	);

	const onDeleteClicked = React.useCallback(() => {
		deleteModal.setIsOpen(true)();
	}, [deleteModal]);

	const onExportCompaniesButtonClicked = () => {
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All ||
			(selectionState === ESelectionState.Some && excludedCompanyIds.length > 0);

		const promise = exportCompaniesMutation.mutateAsync({
			includeContacts: false,
			includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
			...(sendAllSelectedToApi ? buildAllMatchingFilter() : {}),
			excludeCompanyIds: excludedCompanyIds,
		});
		setExportCompaniesPromise(promise);
	};

	const onExportCompaniesAndContactsButtonClicked = () => {
		const sendAllSelectedToApi =
			selectionState === ESelectionState.All ||
			(selectionState === ESelectionState.Some && excludedCompanyIds.length > 0);

		setExportCompaniesPromise(
			exportCompaniesMutation.mutateAsync({
				includeContacts: true,
				includeCompanyIds: sendAllSelectedToApi ? null : selectedCompanyIds,
				...(sendAllSelectedToApi ? buildAllMatchingFilter() : {}),
				excludeCompanyIds: excludedCompanyIds,
			})
		);
	};

	const onExportCompaniesClose = () => {
		setExportCompaniesPromise(null);
	};

	return (
		<div className={css(styleSheet.container)}>
			<button
				className={css(styleSheet.button, styleSheet.merge, !canMerge && styleSheet.disabled)}
				onClick={mergeModal.setIsOpen(true)}
			>
				<span>
					<MergeIcon />
					<span className={css(styleSheet.buttonText)}>Merge</span>
				</span>
			</button>
			<button
				className={css(styleSheet.button, styleSheet.delete, !canDelete && styleSheet.disabled)}
				onClick={onDeleteClicked}
			>
				<span>
					<TrashIcon fillColor={header} />
					<span className={css(styleSheet.buttonText)}>Delete</span>
				</span>
			</button>
			<Dropdown>
				<DropdownTrigger
					render={
						<button className={css(styleSheet.button, styleSheet.export, !canExport && styleSheet.disabled)}>
							<span>
								<ExportIcon />
								<span className={css(styleSheet.buttonText)}>Export</span>
							</span>
						</button>
					}
				/>
				<DropdownContent styles={[bs.w64]}>
					<DropdownItem onClick={onExportCompaniesButtonClicked}>Export only companies</DropdownItem>
					<DropdownItem onClick={onExportCompaniesAndContactsButtonClicked}>
						Export both companies & all contacts
					</DropdownItem>
				</DropdownContent>
			</Dropdown>
			<ExportConfirmationModal
				promise={exportCompaniesPromise}
				modalProps={{
					isOpen: !!exportCompaniesPromise,
					onRequestClose: onExportCompaniesClose,
				}}
				subTitle={
					<>
						<p>The fields we&apos;ll export include: Company Name, Company, Job Title,</p>
						<p>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
					</>
				}
			/>
			<Modal isOpen={mergeModal.isOpen} onRequestClose={mergeModal.onRequestClose}>
				<MergeCompaniesWizard
					selectedCompanies={companies.filter(x => selectedCompanyIds.includes(x.id))}
					onClose={mergeModal.onRequestClose}
				/>
			</Modal>
			<DeleteConfirmation
				bodyText='Are you sure you want to delete these companies?'
				isOpen={deleteModal.isOpen}
				onFinish={deleteModal.onRequestClose}
			/>
		</div>
	);
});
